import { i18n } from 'i18next';
import { batch } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import axios from 'axios';
import { v4 as v4uuid } from 'uuid';
import { setPaymentSelectorError } from '~src/domains/accept/state/acceptReducer';
import { Endpoint, OrderLoadStatus, Page } from '~src/domains/common/enums/api';
import {
  AcceptFormNames,
  FullSSNFormNames,
  PaymentOption,
  QuebecOption,
  VerifyFormNames,
} from '~src/domains/common/enums/common';
import {
  Country,
  Currency,
  Locale,
  RegionsCA,
} from '~src/domains/common/enums/localization';
import { PlaidResponseCode } from '~src/domains/common/enums/plaid';
import * as commonActions from '~src/domains/common/state/commonReducer';
import {
  XhrOrderFailArray,
  XhrPlaidArray,
  XhrPollArray,
  XhrResultsArray,
} from '~src/domains/common/types/api';
import { StateObject } from '~src/domains/common/types/common';
import {
  InvalidAddressOption,
  InvalidAddressType,
} from '~src/domains/invalidAddress/enums/invalidAddress';
import { StartOption } from '~src/domains/start/enums/start';
import { MALU_URL, MAX_POLL_CALLS, XHR_TIMEOUT } from './constants';
import {
  changeLocale,
  chooseDefaultOffer,
  formatDate,
  getCountryDateFormat,
  getMerchantConfig,
  initAndRunLockoutTimer,
  postProcessFunctions,
  sortLoanOffers,
} from './helpers';
import MERCHANT_CONFIGS from './merchantConfigs';
import { paySecureForms } from './secureForms';
import * as trackingUtils from './tracking';

const xhrHeaders = {
  'Content-type': 'application/json',
  Authorization: null,
};

/** Helper function to build error response array */
const buildErrorResponseArray = (
  error: any,
  uuid: string,
): XhrOrderFailArray => {
  const status =
    error.response?.status || (error.code === 'ECONNABORTED' ? 408 : 500);
  const responseData = error.response?.data || { message: error.message };
  return [responseData, status, uuid];
};

/** Helper function to build error response array for Plaid requests */
const buildPlaidErrorResponseArray = (error: any): [any, number] => {
  const status =
    error.response?.status || (error.code === 'ECONNABORTED' ? 408 : 500);
  const responseData = error.response?.data || { response_code: null };
  return [responseData, status];
};

/** Success handler for XHR poll request */
const xhrPollSuccessHandler =
  ([response, status, responseId]: XhrPollArray, state: StateObject) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    const responsePage = response.page;
    const pollCallCount = state.common.pollCallCount;

    if (responsePage) {
      batch(() => {
        dispatch(trackingUtils.trackOfferExpired());
        dispatch(commonActions.setOfferExpired());
      });
    } else {
      batch(() => {
        if (pollCallCount) {
          dispatch(commonActions.setPollCallCount(0));
        }

        if (response.trip_changed) {
          const orderInfo = response.order_info;
          const billingContact = orderInfo?.billing_contact;

          dispatch(trackingUtils.trackTripChanged());

          dispatch(commonActions.setTripChanged(response.kasa_response));
          dispatch(commonActions.setShowBanner(true));
          dispatch(commonActions.updateOrder(orderInfo));

          // Update customer info with billing contact if applicable
          if (billingContact && Object.keys(billingContact).length) {
            dispatch(commonActions.setCustomerInfo(billingContact));
          }
        }
      });
    }
  };

/** Failure handler for XHR poll request */
const xhrPollFailureHandler =
  ([response, status, responseId]: XhrOrderFailArray, state: StateObject) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (status === 410) {
      batch(() => {
        dispatch(trackingUtils.trackOfferExpired());

        // set offer as expired and stop polling
        dispatch(
          commonActions.general({
            isOfferExpired: true,
            pollCallCount: MAX_POLL_CALLS,
            page: Page.TRIP_EXPIRED,
          }),
        );
      });
    } else {
      batch(() => {
        dispatch(
          commonActions.setPollCallCount(state.common.pollCallCount + 1),
        );
        dispatch(trackingUtils.trackXhrPollFail());
      });
    }
  };

/** Timeout handler for XHR poll request */
const xhrPollTimeoutHandler =
  (
    [response, status, responseId]: XhrOrderFailArray,
    uuid: string,
    state: StateObject,
  ) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (response && uuid === responseId) {
      const next = state.common.next as Endpoint;

      batch(() => {
        dispatch(
          trackingUtils.trackXhrResult(
            next,
            status,
            response.mcode as number,
            response.message,
          ),
        );
        dispatch(
          commonActions.general({
            mcode: 8,
            statusCode: status,
            isProcessing: false,
          }),
        );
      });
    }
  };

/** Polls the status of the order. */
const xhrPoll =
  () =>
  (
    dispatch: ThunkDispatch<StateObject, unknown, Action<string>>,
    getState: Function,
  ) => {
    const state = getState();
    const uuid = v4uuid();
    const orderId = state.common.session?.order_id;

    axios({
      method: 'GET',
      url: `${MALU_URL}/tabi/apply/v5/orders/${orderId}/status`,
      headers: xhrHeaders,
      withCredentials: true,
      timeout: XHR_TIMEOUT,
    })
      .then(response => {
        const responseArr: XhrPollArray = [
          response.data,
          response.status,
          uuid,
        ];
        dispatch(xhrPollSuccessHandler(responseArr, state));
      })
      .catch(error => {
        const responseArr = buildErrorResponseArray(error, uuid);

        if (error.code === 'ECONNABORTED') {
          dispatch(xhrPollTimeoutHandler(responseArr, uuid, state));
        } else {
          dispatch(xhrPollFailureHandler(responseArr, state));
        }
      });
  };

/** Success handler for XHR orders request */
const xhrOrdersSuccessHandler =
  ([response, status, responseId]: XhrResultsArray, uuid: string, i18n: i18n) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (responseId !== uuid) {
      dispatch(commonActions.setOrderLoadStatus(OrderLoadStatus.FAILED));
      return;
    }

    const merchantConfig = getMerchantConfig(response, MERCHANT_CONFIGS);
    const billingContact = response.order_info?.billing_contact;

    const localization = response.order_info.localization;
    const locale = `${localization.language}-${localization.country}` as Locale;

    batch(() => {
      dispatch(commonActions.setOrderLoadStatus(OrderLoadStatus.LOADED));

      // Tracking
      dispatch(trackingUtils.trackOrderCreateResultXhr(status, response.mcode));
      dispatch(trackingUtils.trackSelectPayment('Pay Monthly'));
      dispatch(trackingUtils.trackOrderLoad());

      dispatch(changeLocale(locale, i18n));

      // Set state
      dispatch(commonActions.setOrder(response));
      dispatch(commonActions.setMerchantConfig(merchantConfig));
      if (billingContact) {
        dispatch(commonActions.setCustomerInfo(billingContact));
      }
    });
  };

/** Failure handler for XHR orders request */
const xhrOrdersFailureHandler =
  ([response, status, responseId]: XhrOrderFailArray) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    batch(() => {
      dispatch(trackingUtils.trackOrderCreateResultXhr(status, response.mcode));
      dispatch(commonActions.setOrderLoadStatus(OrderLoadStatus.FAILED));
    });
  };

/** Timeout handler for XHR orders request */
const xhrOrdersTimeoutHandler =
  ([response, status, responseId]: XhrOrderFailArray) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    batch(() => {
      dispatch(trackingUtils.trackOrderCreateResultXhr(status, response.mcode));
      dispatch(commonActions.setOrderLoadStatus(OrderLoadStatus.FAILED));
    });
  };

/** Queries the offer status and info.
 * @param {string} offerId - Offer ID.
 */
const xhrOrders =
  (offerId: string, i18n: i18n) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    const uuid = v4uuid();

    batch(() => {
      dispatch(commonActions.setOrderLoadStatus(OrderLoadStatus.LOADING));
      dispatch(trackingUtils.trackOrderCreateXhr());
      dispatch(trackingUtils.trackCheckoutInit());
    });

    axios({
      method: 'GET',
      url: `${MALU_URL}/tabi/apply/v5/offers/${offerId}`,
      headers: xhrHeaders,
      withCredentials: true,
      timeout: XHR_TIMEOUT,
    })
      .then(response => {
        const responseArr: XhrResultsArray = [
          response.data,
          response.status,
          uuid,
        ];
        dispatch(xhrOrdersSuccessHandler(responseArr, uuid, i18n));
      })
      .catch(error => {
        const responseArr = buildErrorResponseArray(error, uuid);

        if (error.code === 'ECONNABORTED') {
          dispatch(xhrOrdersTimeoutHandler(responseArr));
        } else {
          dispatch(xhrOrdersFailureHandler(responseArr));
        }
      });
  };

/** Success handler for XHR next request. */
const xhrNextSuccessHandler =
  (
    [response, status, responseId]: XhrResultsArray,
    uuid: string,
    state: StateObject,
  ) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (responseId !== uuid) return;

    const { next, mcode = null, session, options, page } = response;
    const {
      locale,
      loanOfferSelection,
      customerInfo,
      currency,
      paymentMethod,
    } = state.common;
    const [_, country] = locale.split('-');

    const sessionLoanOffers =
      session.product_offers?.offers || session.loan_offers;
    const loanOffers = sortLoanOffers(sessionLoanOffers);
    const isMultipleOfferFlow = loanOffers.length > 1;
    const loanOfferUnchanged = loanOffers.some(
      offer => offer.offer_id === loanOfferSelection,
    );
    const newLoanOfferSelection = loanOfferUnchanged
      ? loanOfferSelection
      : chooseDefaultOffer(loanOffers, page)?.offer_id;

    const defaultDispatch = () => {
      dispatch(
        commonActions.general({
          session,
          options,
          isMultipleOfferFlow,
          page,
          mcode,
          next,
          loanOfferSelection: newLoanOfferSelection,
          customerInfo: {
            ...customerInfo,
            mobile_verification_code: '',
          },
          statusCode: status,
          showBanner: false,
        }),
      );
      dispatch(
        trackingUtils.trackXhrResult(
          state.common.next as Endpoint,
          status,
          mcode as number,
          next,
        ),
      );
      dispatch(commonActions.resetLockoutTimer());
    };

    // if WEX is issued
    if (mcode === 3 && page === Page.ACCEPT) {
      batch(() => {
        dispatch(
          commonActions.general({
            mcode,
            isProcessing: false,
          }),
        );
        dispatch(initAndRunLockoutTimer());
      });
    } else if (
      next === Endpoint.CARD &&
      options.includes('ssn') &&
      country !== Country.CA
    ) {
      const isBankAccountSelected =
        paymentMethod === PaymentOption.BANK_ACCOUNT;
      const acceptForm = state.acceptForm;
      const nameOnCard = isBankAccountSelected
        ? ''
        : (acceptForm['card-info-name'].value as string);
      batch(() => {
        defaultDispatch();
        dispatch(trackingUtils.trackAuth(paymentMethod as PaymentOption));
        dispatch(
          commonActions.general({
            isProcessing: true,
          }),
        );
      });

      if (isBankAccountSelected) {
        dispatch(xhrNext({ forceNext: Endpoint.ACCEPT }));
      } else {
        paySecureForms(nameOnCard, currency as Currency);
      }
    } else {
      batch(() => {
        defaultDispatch();
        dispatch(
          commonActions.general({
            isProcessing: false,
            lazyDocsInFlight: page === Page.LOAN_CONSENT,
            last4: session.card?.last4 || session.bank?.last4 || null,
            cardType: session.card?.type || null,
          }),
        );
      });
    }
  };

/** Fail handler for XHR next request. */
const xhrNextFailHandler =
  ([response, status, responseId]: XhrOrderFailArray, prevStep: Endpoint) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    const page = response.page;
    const startLockout = [401, 500, 502, 503].includes(status);
    batch(() => {
      dispatch(
        trackingUtils.trackXhrResult(
          prevStep,
          status,
          response.mcode as number,
          response.message,
        ),
      );
      dispatch(
        commonActions.general({
          isProcessing: false,
          statusCode: status,
        }),
      );
      if (page) {
        dispatch(commonActions.setPage(response.page));
      }
      if (startLockout) {
        dispatch(commonActions.setMcode(3));
        dispatch(initAndRunLockoutTimer());
      } else {
        dispatch(commonActions.setMcode(response.mcode));
      }
    });
  };

/** Timeout handler for XHR next request. */
const xhrNextTimeoutHandler =
  (
    [response, status, responseId]: XhrOrderFailArray,
    uuid: string,
    prevStep: Endpoint,
  ) =>
  (dispatch: ThunkDispatch<StateObject, unknown, Action<string>>) => {
    if (responseId !== uuid) {
      return;
    }

    batch(() => {
      dispatch(
        trackingUtils.trackXhrResult(
          prevStep,
          status,
          response.mcode as number,
          response.message,
        ),
      );
      dispatch(
        commonActions.general({
          isProcessing: false,
          mcode: 8,
          statusCode: status,
        }),
      );
    });
  };

/** Generates a data payload given a next endpoint.
 * @param {Endpoint} next - Next endpoint.
 * @param {StateObject} state - Current state.
 */
const getNextData = (
  next: Endpoint,
  state: StateObject,
  resending?: boolean,
) => {
  const { customerInfo, locale, bankMetadata, paymentMethod, page } =
    state.common;
  const fullSSNForm = state.fullSSNForm;
  const startForm = state.startForm;
  const acceptForm = state.acceptForm;
  const invalidAddressForm = state.invalidAddressForm;
  const reacceptForm = state.reacceptForm;
  const autoPayEnabled =
    page === Page.ACCEPT
      ? acceptForm[AcceptFormNames.AUTOPAY].value
      : reacceptForm[AcceptFormNames.AUTOPAY].value;
  const [language, country] = locale.split('-');
  const bankAccountSelected = paymentMethod === PaymentOption.BANK_ACCOUNT;
  const dateFormat = getCountryDateFormat(country as Country);
  const dateOfBirth = startForm.date_of_birth.value as string;
  const formattedDob =
    dateOfBirth.length > 0 && formatDate(dateOfBirth, dateFormat);
  const currPage = state.common.page;
  const quebecForm =
    currPage === Page.INVALID_ADDRESS ? invalidAddressForm : startForm;

  switch (next) {
    // Defaulting to null for APPLY and APPLY_QUEBEC for resending mobile verification code scenario where store is reset
    case Endpoint.APPLY:
      const phoneNumber =
        startForm[StartOption.MOBILE].value || customerInfo.phone;

      return {
        country: country || null,
        language: language || null,
        marketing_communication_opt_in: resending
          ? null
          : state.start.marketingCommunicationOptIn,
        first_name: resending
          ? null
          : startForm[StartOption.FIRST_NAME].value ||
            customerInfo.first_name ||
            null,
        last_name: resending
          ? null
          : startForm[StartOption.LAST_NAME].value ||
            customerInfo.last_name ||
            null,
        date_of_birth: formattedDob || null,
        street_address: startForm[StartOption.ADDRESS].value || null,
        city: startForm[StartOption.CITY].value || null,
        region: startForm[StartOption.REGION].value || null,
        postal_code: startForm[StartOption.POSTAL_CODE].value
          ? postProcessFunctions.digitCharOnlyString(
              startForm.postal_code.value,
            )
          : null,
        email: startForm[StartOption.EMAIL].value || customerInfo.email || null,
        mobile: phoneNumber
          ? postProcessFunctions.digitOnlyString(phoneNumber)
          : null,
        stated_income: startForm[StartOption.INCOME].value
          ? postProcessFunctions.priceInCents(startForm.income.value)
          : null,
        ssn: startForm[StartOption.SSN].value || null,
      };
    case Endpoint.APPLY_QUEBEC:
      return {
        monthly_housing_costs:
          quebecForm[QuebecOption.MONTHLY_HOUSING_COST].value != undefined
            ? postProcessFunctions.priceInCents(
                quebecForm[QuebecOption.MONTHLY_HOUSING_COST].value,
              )
            : null,
        occupation: quebecForm[QuebecOption.OCCUPATION].value || null,
        primary_source_income:
          quebecForm[QuebecOption.PRIMARY_INCOME_SOURCE].value || null,
        duration_current_employer:
          quebecForm[QuebecOption.EMPLOYMENT_DURATION].value || null,
      };
    case Endpoint.VERIFY:
      const verifyForm = state.verifyForm;
      return {
        code: verifyForm[VerifyFormNames.MOBILE_VERIFICATION_CODE].value,
      };
    case Endpoint.SSN:
      const fullSsn = acceptForm[AcceptFormNames.FULL_SSN].value;
      return {
        code: fullSsn ? postProcessFunctions.digitOnlyString(fullSsn) : null,
      };
    case Endpoint.START:
      return {};
    case Endpoint.ACCEPT:
      return {
        'rege_opt-in': autoPayEnabled,
        public_token: bankAccountSelected ? bankMetadata?.public_token : null,
        selected_account_ref: bankAccountSelected
          ? bankMetadata?.account.id
          : null,
      };
    case Endpoint.ACCEPT_MULTIPLE_OFFER:
      return {
        selected_offer: state.common.loanOfferSelection,
        'rege_opt-in': autoPayEnabled,
        public_token: bankAccountSelected ? bankMetadata?.public_token : null,
        selected_account_ref: bankAccountSelected
          ? bankMetadata?.account.id
          : null,
      };
    case Endpoint.LOAN_CONSENT:
      return {
        signed: true,
      };
    case Endpoint.UPDATE_APPLICATION:
      const addressType = state.invalidAddress.addressType;
      const originalAddress = state.common.session?.original_address;
      const basePayload = {
        country: country || null,
        language: language || null,
        first_name: null,
        last_name: null,
        date_of_birth: formattedDob || null,
        email: customerInfo.email || null,
        mobile: customerInfo.phone
          ? postProcessFunctions.digitOnlyString(customerInfo.phone)
          : null,
      };
      const addressPayload =
        addressType === InvalidAddressType.CUSTOM
          ? {
              street_address:
                invalidAddressForm[InvalidAddressOption.ADDRESS].value || null,
              city: invalidAddressForm[InvalidAddressOption.CITY].value || null,
              region:
                invalidAddressForm[InvalidAddressOption.REGION].value || null,
              postal_code: invalidAddressForm[InvalidAddressOption.POSTAL_CODE]
                .value
                ? postProcessFunctions.digitCharOnlyString(
                    invalidAddressForm[InvalidAddressOption.POSTAL_CODE].value,
                  )
                : null,
            }
          : {
              street_address:
                originalAddress?.[InvalidAddressOption.ADDRESS] || null,
              city: originalAddress?.[InvalidAddressOption.CITY] || null,
              region: originalAddress?.[InvalidAddressOption.REGION] || null,
              postal_code: originalAddress?.[InvalidAddressOption.POSTAL_CODE]
                ? postProcessFunctions.digitCharOnlyString(
                    originalAddress?.[InvalidAddressOption.POSTAL_CODE],
                  )
                : null,
            };

      return {
        ...basePayload,
        ...addressPayload,
      };
    case Endpoint.FULL_SSN:
      const fullSSNValue = fullSSNForm[FullSSNFormNames.FULL_SSN].value;
      return {
        code: fullSSNValue
          ? postProcessFunctions.digitOnlyString(fullSSNValue)
          : null,
      };
    default:
      return {};
  }
};

/** Makes a post request to the next endpoint and handles the response.
 * @param {Endpoint} [forceNext] - Endpoint to make the request.
 */
const xhrNext =
  (config?: { forceNext?: Endpoint; resending?: boolean }) =>
  (
    dispatch: ThunkDispatch<StateObject, unknown, Action<string>>,
    getState: Function,
  ) => {
    const state = getState();

    const {
      locale,
      isMultipleOfferFlow,
      customerInfo,
      autoPayEnabled,
      paymentOptions,
      paymentMethod,
      currency,
      session,
      next: previousStep,
    } = state.common;

    const addressType = state.invalidAddress.addressType;

    const orderId = session.order_id;
    const next = config?.forceNext || previousStep;
    const [language, country] = locale.split('-');

    dispatch(commonActions.setIsProcessing(true));

    if (next === Endpoint.CARD) {
      const acceptForm = state.acceptForm;
      const nameOnCard =
        paymentMethod === PaymentOption.BANK_ACCOUNT
          ? ''
          : acceptForm['card-info-name'].value;
      const isCreditEnabled = paymentOptions.find(
        (option: PaymentOption) => option === PaymentOption.CREDIT,
      );
      if (isCreditEnabled) {
        dispatch(trackingUtils.trackAuth(state.common.paymentMethod));
      }
      paySecureForms(nameOnCard, currency);
    } else {
      const uuid = v4uuid();
      // If the next endpoint is accept and there are multiple offers, use the multiple offer accept endpoint to generate data payload
      const transformedNext =
        next === Endpoint.ACCEPT && isMultipleOfferFlow
          ? Endpoint.ACCEPT_MULTIPLE_OFFER
          : next;
      // Takes the next endpoint and uses getNextData to generate map the endpoint to a data payload
      const nextData = getNextData(transformedNext, state, config?.resending);

      /** Generates data payload for Quebec
       * 1. If next endpoint is /apply and the start form region is Quebec, generate Quebec payload.
       * 2. If next endpoint is /update/application and the invalid address form region is Quebec, generate Quebec payload.
       * Otherwise, data payload is empty object. */
      const quebecData =
        (next === Endpoint.APPLY &&
          state.startForm.region.value === RegionsCA.QC) ||
        (next === Endpoint.UPDATE_APPLICATION &&
          addressType === InvalidAddressType.CUSTOM &&
          state.invalidAddressForm.region.value === RegionsCA.QC)
          ? getNextData(Endpoint.APPLY_QUEBEC, state, config?.resending)
          : {};

      const dataPayload = JSON.stringify({
        ...nextData,
        ...quebecData,
        'ui-language': language,
      });

      if (next === Endpoint.ACCEPT) {
        nextData['rege_opt-in'] = autoPayEnabled;
      }

      dispatch(trackingUtils.trackXhr());

      axios({
        method: 'POST',
        url: `${MALU_URL}/tabi/apply/v5/${orderId}${next}`,
        headers: xhrHeaders,
        data: dataPayload,
        withCredentials: true,
        timeout: XHR_TIMEOUT,
      })
        .then(response => {
          const responseArr: XhrResultsArray = [
            response.data,
            response.status,
            uuid,
          ];
          dispatch(xhrNextSuccessHandler(responseArr, uuid, state));
        })
        .catch(error => {
          const responseArr = buildErrorResponseArray(error, uuid);

          if (error.code === 'ECONNABORTED') {
            dispatch(xhrNextTimeoutHandler(responseArr, uuid, previousStep));
          } else {
            dispatch(xhrNextFailHandler(responseArr, previousStep));
          }
        });
    }
  };

/** Makes a POST request to get a Plaid link token */
const xhrPlaidLinkToken =
  (locale: Locale, tokenRefresh: boolean = false) =>
  (
    dispatch: ThunkDispatch<StateObject, unknown, Action<string>>,
    getState: Function,
  ) => {
    const state = getState();
    const orderId = state.common.session?.order_id;
    const [language, _] = locale.split('-');

    const handlePlaidLinkTokenError = (responseCode: number) => {
      const isPlaidDisabled = responseCode === PlaidResponseCode.DISABLED;
      batch(() => {
        dispatch(commonActions.setPlaidLinkToken(null));
        dispatch(commonActions.setPaymentMethod(PaymentOption.DEBIT));
        dispatch(
          setPaymentSelectorError(
            isPlaidDisabled
              ? 'accept:plaid-locked-error'
              : 'accept:plaid-link-token-error',
          ),
        );

        if (isPlaidDisabled) {
          dispatch(commonActions.setPlaidDisabled());
        }
      });
    };

    dispatch(trackingUtils.trackXhrLinkToken());

    axios({
      method: 'POST',
      url: `${MALU_URL}/plaid/${orderId}/link-token`,
      headers: xhrHeaders,
      data: JSON.stringify({
        language_code: language,
        token_refresh: tokenRefresh,
      }),
      withCredentials: true,
      timeout: XHR_TIMEOUT,
    })
      .then(response => {
        const responseArr: XhrPlaidArray = [
          response.data,
          response.status,
          orderId,
        ];
        const [responseData, status, _] = responseArr;
        const responseCode = responseData.response_code;

        dispatch(trackingUtils.trackXhrLinkToken(responseData, status));
        if (
          responseCode === PlaidResponseCode.SUCCESS &&
          responseData.link_token
        ) {
          batch(() => {
            dispatch(commonActions.setPlaidLinkToken(responseData.link_token));
            dispatch(setPaymentSelectorError(null));
          });
        } else {
          handlePlaidLinkTokenError(responseCode);
        }
      })
      .catch(error => {
        const [responseData, status] = buildPlaidErrorResponseArray(error);

        dispatch(trackingUtils.trackXhrLinkToken(responseData, status));
        handlePlaidLinkTokenError(responseData.response_code);
      });
  };

export {
  xhrPlaidLinkToken,
  xhrPollSuccessHandler,
  xhrPollFailureHandler,
  xhrPollTimeoutHandler,
  xhrPoll,
  xhrOrdersSuccessHandler,
  xhrOrdersFailureHandler,
  xhrOrdersTimeoutHandler,
  xhrOrders,
  xhrNextSuccessHandler,
  xhrNextFailHandler,
  xhrNextTimeoutHandler,
  xhrNext,
};
